import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

export interface ElderlyVitalRecord {
  id: number;
  dependant: number | null;
  dependant_name: string | null;
  metric_type: string;
  value: number;
  value_secondary: number | null;
  unit: string;
  source: string;
  notes: string;
  priority: string;
  recorded_at: string;
  display_value: string;
  is_abnormal: boolean;
  created_at: string;
  updated_at: string;
}

export interface ElderlyVitalPayload {
  dependant?: number | null;
  metric_type: string;
  value: number;
  value_secondary?: number | null;
  unit: string;
  source?: string;
  notes?: string;
  recorded_at: string;
}

export interface ElderlyVitalAlert {
  id: number;
  vital_record: ElderlyVitalRecord;
  severity: string;
  message: string;
  is_read: boolean;
  is_resolved: boolean;
  created_at: string;
}

export interface ElderlyVitalSummary {
  metric_type: string;
  latest_value: number;
  latest_value_secondary: number | null;
  unit: string;
  recorded_at: string;
  is_abnormal: boolean;
  trend: string;
  record_count: number;
}

export interface ElderlyMember {
  id: number | null;
  name: string;
  relationship: string;
  dob: string | null;
  age: number | null;
  gender: string | null;
  is_elderly: boolean;
  has_linked_user: boolean;
}

export interface ElderlyMembersResponse {
  user_age: number | null;
  user_is_elderly: boolean;
  is_primary: boolean;
  members: ElderlyMember[];
}

export interface ElderlyCareService {
  id: number;
  name: string;
  description: string;
  icon: string;
  sort_order: number;
  is_active: boolean;
}

export interface ElderlyCareCategory {
  id: number;
  slug: string;
  name: string;
  icon: string;
  accent_color: string;
  light_color: string;
  image: string;
  description_title: string;
  description_subtitle: string;
  sort_order: number;
  is_active: boolean;
  services: ElderlyCareService[];
}

const elderlyCareService = {
  // ─── Page content (tabs, descriptions, services) ──────

  async getContent(): Promise<ElderlyCareCategory[]> {
    const response = await axiosInstance.get(API_ENDPOINTS.elderlyCare.categories);
    const d = response.data;
    return Array.isArray(d) ? d : d.results || [];
  },

  // ─── Members ───────────────────────────────────────────

  async getMembers(): Promise<ElderlyMembersResponse> {
    const response = await axiosInstance.get(API_ENDPOINTS.elderlyCare.members);
    return response.data;
  },

  // ─── Vitals ─────────────────────────────────────────────

  async getVitals(params?: { metric_type?: string; days?: number; dependant?: number }): Promise<ElderlyVitalRecord[]> {
    const response = await axiosInstance.get(API_ENDPOINTS.elderlyCare.vitals, { params });
    const d = response.data;
    return Array.isArray(d) ? d : d.results || [];
  },

  async createVital(data: ElderlyVitalPayload): Promise<ElderlyVitalRecord> {
    const response = await axiosInstance.post(API_ENDPOINTS.elderlyCare.vitals, data);
    return response.data;
  },

  async createVitals(data: ElderlyVitalPayload[]): Promise<ElderlyVitalRecord[]> {
    const response = await axiosInstance.post(API_ENDPOINTS.elderlyCare.vitalsBulk, { records: data });
    return response.data;
  },

  async deleteVital(id: number): Promise<void> {
    await axiosInstance.delete(`${API_ENDPOINTS.elderlyCare.vitals}${id}/`);
  },

  async getLatest(metricType?: string, dependant?: number): Promise<ElderlyVitalRecord | null> {
    try {
      const params: any = {};
      if (metricType) params.metric_type = metricType;
      if (dependant) params.dependant = dependant;
      const response = await axiosInstance.get(API_ENDPOINTS.elderlyCare.vitalsLatest, { params });
      return response.data;
    } catch {
      return null;
    }
  },

  async getLatestAll(dependant?: number): Promise<Record<string, ElderlyVitalRecord>> {
    try {
      const params: any = {};
      if (dependant) params.dependant = dependant;
      const response = await axiosInstance.get(API_ENDPOINTS.elderlyCare.vitalsLatestAll, { params });
      return response.data;
    } catch {
      return {};
    }
  },

  async getSummary(days?: number, dependant?: number): Promise<ElderlyVitalSummary[]> {
    const params: any = {};
    if (days) params.days = days;
    if (dependant) params.dependant = dependant;
    const response = await axiosInstance.get(API_ENDPOINTS.elderlyCare.vitalsSummary, { params });
    return response.data;
  },

  async getTrend(metricType: string, days?: number, dependant?: number): Promise<ElderlyVitalRecord[]> {
    const params: any = { metric_type: metricType };
    if (days) params.days = days;
    if (dependant) params.dependant = dependant;
    const response = await axiosInstance.get(API_ENDPOINTS.elderlyCare.vitalsTrend, { params });
    return response.data;
  },

  async getChoices(): Promise<{
    metric_types: Record<string, string>;
    sources: Record<string, string>;
    priorities: Record<string, string>;
    normal_ranges: Record<string, any>;
  }> {
    const response = await axiosInstance.get(API_ENDPOINTS.elderlyCare.vitalsChoices);
    return response.data;
  },

  async getStats(dependant?: number): Promise<any> {
    const params: any = {};
    if (dependant) params.dependant = dependant;
    const response = await axiosInstance.get(API_ENDPOINTS.elderlyCare.vitalsStats, { params });
    return response.data;
  },

  // ─── Alerts ─────────────────────────────────────────────

  async getAlerts(opts?: { unread?: boolean; unresolved?: boolean; dependant?: number }): Promise<ElderlyVitalAlert[]> {
    const params: any = {};
    if (opts?.unread) params.unread = 'true';
    if (opts?.unresolved) params.unresolved = 'true';
    if (opts?.dependant) params.dependant = opts.dependant;
    const response = await axiosInstance.get(API_ENDPOINTS.elderlyCare.alerts, { params });
    const d = response.data;
    return Array.isArray(d) ? d : d.results || [];
  },

  async getUnreadAlertCount(dependant?: number): Promise<number> {
    const params: any = {};
    if (dependant) params.dependant = dependant;
    const response = await axiosInstance.get(API_ENDPOINTS.elderlyCare.alertsUnreadCount, { params });
    return response.data?.unread ?? 0;
  },

  async markAlertRead(id: number): Promise<ElderlyVitalAlert> {
    const response = await axiosInstance.post(API_ENDPOINTS.elderlyCare.alertMarkRead(id));
    return response.data;
  },

  async resolveAlert(id: number): Promise<ElderlyVitalAlert> {
    const response = await axiosInstance.post(API_ENDPOINTS.elderlyCare.alertResolve(id));
    return response.data;
  },

  async markAllAlertsRead(dependant?: number): Promise<void> {
    const params: any = {};
    if (dependant) params.dependant = dependant;
    await axiosInstance.post(API_ENDPOINTS.elderlyCare.alertsMarkAllRead, null, { params });
  },

  // ─── Enquiries ─────────────────────────────────────────

  async createEnquiry(data: {
    service: number;
    dependant?: number | null;
    name?: string;
    phone?: string;
    email?: string;
    message?: string;
    preferred_time?: string;
  }): Promise<any> {
    const response = await axiosInstance.post(API_ENDPOINTS.elderlyCare.enquiries, data);
    return response.data;
  },

  async getAdminEnquiries(params?: { search?: string; status?: string; page?: number; page_size?: number }): Promise<any[]> {
    const response = await axiosInstance.get(API_ENDPOINTS.elderlyCare.enquiries, { params });
    const d = response.data;
    return Array.isArray(d) ? d : d.results || [];
  },

  async updateEnquiryStatus(id: number, status: string): Promise<any> {
    const response = await axiosInstance.patch(API_ENDPOINTS.elderlyCare.enquiryStatus(id), { status });
    return response.data;
  },
};

export default elderlyCareService;
